import { useCallback } from 'react'
import { useMsal } from '@azure/msal-react'
import { loginRequest } from '../../../../authConfig'
import { getConfig } from '../../../../configs/config'

// Thrown by fetchAuthenticatedResponse so callers can distinguish access-denied (401/403, e.g. a
// SharePoint-backed document the user lacks permission to) from other failures (network, 5xx).
export class AuthenticatedFetchError extends Error {
  status: number
  constructor(status: number) {
    super(`Download failed: ${status}`)
    this.status = status
  }
}

/**
 * Hook that provides an authenticated file download function.
 * Validates the URL against trusted API domains before attaching a Bearer token,
 * then triggers a browser download from the fetched blob.
 */
export const useAuthenticatedDownload = () => {
  const { instance, accounts } = useMsal()
  const config = getConfig()

  const trustedHosts = useCallback(
    () => ({
      default: config.REACT_APP_API_URL
        ? new URL(config.REACT_APP_API_URL).hostname.toLowerCase()
        : null,
      nexus: config.REACT_NEXUS_API_URL
        ? new URL(config.REACT_NEXUS_API_URL).hostname.toLowerCase()
        : null,
      semanticdata: config.REACT_SEMANTIC_API_URL
        ? new URL(config.REACT_SEMANTIC_API_URL).hostname.toLowerCase()
        : null,
      smartdocs: config.REACT_SMARTDOCS_API_URL
        ? new URL(config.REACT_SMARTDOCS_API_URL).hostname.toLowerCase()
        : null
    }),
    [config]
  )

  const isTrustedUrl = useCallback(
    (url: string) => {
      const urlHostname = new URL(url).hostname.toLowerCase()
      return Object.values(trustedHosts()).some((domain) => domain && urlHostname === domain)
    },
    [trustedHosts]
  )

  // Mirrors useFetch's app -> scope mapping: nexus/semanticdata get the agents scope, every other
  // API (default "inv" API and SmartDocs) authenticates with the default app scope. Selecting the
  // scope from the URL's own host (rather than hardcoding one scope for every download) is required
  // because this hook is shared by nexus-hosted chat artifacts AND non-nexus citation documents.
  const getScopeForUrl = useCallback(
    (url: string) => {
      const hosts = trustedHosts()
      const urlHostname = new URL(url).hostname.toLowerCase()
      return urlHostname === hosts.nexus || urlHostname === hosts.semanticdata
        ? config.REACT_NEXUS_AZURE_SCOPE
        : config.REACT_APP_AZURE_SCOPE
    },
    [trustedHosts, config]
  )

  const fetchAuthenticatedResponse = useCallback(
    async (url: string) => {
      if (!isTrustedUrl(url)) {
        console.error('Download blocked: untrusted domain', new URL(url).hostname.toLowerCase())
        return null
      }

      const request = {
        ...loginRequest,
        scopes: [getScopeForUrl(url)],
        account: accounts[0]
      }
      const msalResponse = await instance.acquireTokenSilent(request)
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${msalResponse?.accessToken}` }
      })
      if (!response.ok) throw new AuthenticatedFetchError(response.status)
      return response
    },
    [instance, accounts, isTrustedUrl, getScopeForUrl]
  )

  const downloadWithAuth = useCallback(
    async (url: string, filename: string) => {
      try {
        const response = await fetchAuthenticatedResponse(url)
        if (!response) return
        const blob = await response.blob()
        const blobUrl = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = blobUrl
        anchor.download = filename
        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)
        URL.revokeObjectURL(blobUrl)
      } catch (error) {
        console.error('Authenticated download failed:', error)
      }
    },
    [fetchAuthenticatedResponse]
  )

  /**
   * Fetches a document as a Blob URL for in-app preview (no forced browser download).
   * Reuses the same trusted-domain check and MSAL token acquisition as downloadWithAuth.
   * Caller is responsible for revoking the returned blobUrl via URL.revokeObjectURL when done.
   */
  const fetchAuthenticatedBlobUrl = useCallback(
    async (url: string): Promise<{ blobUrl: string; mimeType: string } | null> => {
      const response = await fetchAuthenticatedResponse(url)
      if (!response) return null
      const blob = await response.blob()
      const mimeType = blob.type || 'application/octet-stream'
      return { blobUrl: URL.createObjectURL(blob), mimeType }
    },
    [fetchAuthenticatedResponse]
  )

  return { downloadWithAuth, fetchAuthenticatedBlobUrl }
}
